import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
  useRouteError,
} from 'react-router-dom';
import { AxiomProvider } from '@optiaxiom/react';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { loadOptiWebComponents } from './utils/loadOptiWebComponents.ts';
import { SentryErrorBoundary } from './utils/sentry.ts';
import App from './App.tsx';

import '@optimizely/axiom-fonts';
import './utils/fullstory.ts';
import './styles/globals.css.ts';

const queryClient = new QueryClient();

const persister = createAsyncStoragePersister({ storage: window.localStorage });

export const RethrowError = () => {
  throw useRouteError();
};

const router = createBrowserRouter(
  createRoutesFromElements(<Route path="*" element={<App />} errorElement={<RethrowError />} />),
  { basename: process.env.PREVIEW_BASE_PATH || undefined }
);

loadOptiWebComponents().catch(console.error);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}
createRoot(rootElement).render(
  <StrictMode>
    <SentryErrorBoundary>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister,
          dehydrateOptions: {
            shouldDehydrateQuery: query => query.meta?.persist === true,
          },
        }}
      >
        <AxiomProvider>
          <RouterProvider router={router} />
        </AxiomProvider>
      </PersistQueryClientProvider>
    </SentryErrorBoundary>
  </StrictMode>
);
